/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Subject } from 'rxjs';
import moment from 'moment';
import { WorkBook, WorkSheet, Sheet, read, writeFile, utils } from 'xlsx';
import {
	ISpreadsheetValidation,
	SpreadsheetBase,
	SpreadsheetExceptionType,
	SpreadsheetInputType,
} from './spreadsheet-base';
import { IProjectTicketListMetadata } from '../project-tickets.service';
import { TicketFields } from '@touchplan/common';
import {
	EXTERNAL_SCHEDULE_SPREADSHEET_ERROR_MISSING_REQUIRED_COLUMNS,
	EXTERNAL_SCHEDULE_SPREADSHEET_ERROR_MISSING_REQUIRED_SHEETS,
} from '@touchplan/strings';

export interface IExternalScheduleTicketData {
	// used to update ticket.externalScheduleFinishDate
	projectId: string;
	planId: string;
	ticketId: string;
	finishDate: string;
}

interface IColumnData {
	index: number;
	letter: string;
}

interface IExternalScheduleExceptionData {
	id: string;
	activityName: string;
	planId: string;
	ticketUrl: string;
}

enum SheetName {
	Task = 'TASK',
	UserData = 'USERDATA',
}

enum ColumnName {
	ActivityId = 'Activity ID',
	wBSCode = 'WBS Code',
	Finish = '(*)Finish', // for date format and saving to data filed in ticket
	touchplanFinish = 'Touchplan Finish',
	touchplanTicketURL = 'Touchplan Ticket URL', // optional
}

type RequiredFields = SheetName | ColumnName;

export class ExternalScheduleSpreadsheet extends SpreadsheetBase {
	public ticketToUpdate$: Subject<IExternalScheduleTicketData>;

	private readonly P6_DATE_FORMAT = 'M/d/yyyy hh:mm';
	private readonly USER_DATA_CELL = 'A3';
	private readonly NUM_HEADERS = 2;

	private externalScheduleIdMap: Map<string, IExternalScheduleExceptionData[]>; // external schedule id -> list of ticket ids
	private taskSheet: WorkSheet;
	private matchCount = 0;
	private activityIdColumn: IColumnData;
	private finishColumn: IColumnData;
	private tpFinishColumn: IColumnData;
	private tpTicketUrl: IColumnData;

	private dateFormat: string;

	constructor() {
		super();
		this.ticketToUpdate$ = new Subject<IExternalScheduleTicketData>();
		this.externalScheduleIdMap = new Map<string, IExternalScheduleExceptionData[]>();
	}

	/**
	 * Validate the spreadsheet and return validation info
	 * @param file spreadsheet
	 * @param inputType spreadsheet read type
	 * @returns ISpreadsheetValidation
	 */
	public validate(file: string, inputType: SpreadsheetInputType): ISpreadsheetValidation {
		return this.parse(file, inputType);
	}

	public prepare(): void {
		this.taskSheet = this.workbook.Sheets[SheetName.Task]; // object
		// for write back to spreadsheet
		// get Touchplan Finish column, e.g. "E" in Excel spreadsheet column
		const sheetRefArr: string[] = this.workbook.Sheets[SheetName.Task]['!ref'].split(':');
		const startingColumn: string = sheetRefArr[0].substring(0, 1).toUpperCase();
		const endingColumn: string = sheetRefArr[1].substring(0, 1).toUpperCase();
		const allColumns: string[] = [];
		for (let i = startingColumn.charCodeAt(0); i <= endingColumn.charCodeAt(0); i++) {
			allColumns.push(String.fromCharCode(i));
		}

		const columnNames: string[] = Object.values(this.sheet[1]);
		for (let i = 0; i < columnNames.length; i++) {
			switch (columnNames[i]) {
				case ColumnName.ActivityId:
					this.activityIdColumn = {
						index: i,
						letter: allColumns[i],
					};
					break;
				case ColumnName.Finish:
					this.finishColumn = {
						index: i,
						letter: allColumns[i],
					};
					break;
				case ColumnName.touchplanFinish:
					this.tpFinishColumn = {
						index: i,
						letter: allColumns[i],
					};
					break;
				case ColumnName.touchplanTicketURL:
					this.tpTicketUrl = {
						index: i,
						letter: allColumns[i],
					};
					break;
			}
		}

		/*
		 * dev note:
		 * Basically P6 complains about our exported spreadsheet
		 * Because USERDATA sheet shouldn't be edited, but SheetJS parses CR/CR LF in the cell and replaces them with x00d
		 * While writing data back to spreadsheet, x00d gets preserved and upsets P6 spreadsheet validation
		 * So... we are replacing CR/CR LF with LF
		 */
		const userDataSheet = this.workbook.Sheets[SheetName.UserData]; // object
		let cell: string;
		for (let i = 1; i <= 3; i++) {
			// update values from A1-A3
			cell = `A${i}`;
			userDataSheet[cell].v = userDataSheet[cell].v.replace(/(?:\r\n|\r)/g, '\n');
			userDataSheet[cell].h = userDataSheet[cell].v;
		}

		const userDataArr = userDataSheet[this.USER_DATA_CELL]?.v?.split('\n');
		if (userDataArr) {
			for (const item of userDataArr) {
				if (item.includes('DateFormat')) {
					const dateFormat = item.split('=')[1].replace(/'/g, ''); // some date format has weird single quotes, remove them...
					const arr = dateFormat.split(' '); // based on specification, date & time are separated by space
					this.dateFormat = arr[0].toUpperCase(); // time format is being derived below since we have to best guess 12/24 format
					break;
				}
			}
		}
		this.dateFormat ||= this.P6_DATE_FORMAT;
	}

	/**
	 * Process spreadsheet, it uses exception$ to notify caller of any ticket data issues
	 * @param data all tickets from one plan
	 */
	public process<T extends IProjectTicketListMetadata>(data: T): void {
		for (const ticket of data.list) {
			// externalScheduleId is the new data field of ticket...
			const { externalScheduleId } = ticket;
			// don't need to check if no external schedule id (0 should be safe since it's a string "0" according to the parser)
			if (!externalScheduleId) {
				continue;
			}

			const ticketUrl = this.generateTpTicketUrl(data.projectId, data.planId, ticket.id);
			let found = false;
			for (let j = this.NUM_HEADERS; j < this.sheet.length; j++) {
				if (externalScheduleId === this.sheet[j][this.activityIdColumn.index]) {
					const p6FinishDate = this.taskSheet[`${this.finishColumn.letter}${j + 1}`];
					// dayjs fails on some date validation without 2nd arg of date format
					const p6FinishDateValid = p6FinishDate && moment(p6FinishDate.w, this.dateFormat).isValid();

					if (p6FinishDateValid) {
						// get data from spreadsheet to update ticket externalScheduleFinishDate
						this.ticketToUpdate$.next({
							projectId: data.projectId,
							planId: data.planId,
							ticketId: ticket.id,
							finishDate: p6FinishDate.w,
						});
					}

					// write ticket data to spreadsheet
					const cell = `${this.tpFinishColumn.letter}${j + 1}`;
					const ticketFinishDate = ticket.actualFinish || ticket.plannedFinish;

					// skip the write for pull side tickets, since they don't have finish date...
					if (ticketFinishDate) {
						const p6FinishDateTime = p6FinishDate.w.split(' ').slice(1).join(' ');
						// generate time based on (*) Finish cell
						let outputDateTime: string;
						let timeFormat = '';
						if (p6FinishDateTime) {
							if (p6FinishDateValid && p6FinishDate.w) {
								outputDateTime = `${ticketFinishDate} ${p6FinishDateTime}`; // valid time
							} else {
								outputDateTime = `${ticketFinishDate} 00:00`; // invalid time
							}

							// this would incorrectly treat 10/11/12 as "h" format, but it's fine with P6
							const hourHasLeadingZero = p6FinishDateTime.substring(0, 1) === '0';
							if (
								p6FinishDate.w.toLowerCase().includes('am') ||
								p6FinishDate.w.toLowerCase().includes('pm')
							) {
								timeFormat = hourHasLeadingZero ? ' hh:mm A' : ' h:mm A';
							} else {
								timeFormat = hourHasLeadingZero ? ' HH:mm' : ' H:mm';
							}
						} else {
							outputDateTime = `${ticketFinishDate}`; // (*) Finish has no time
						}

						this.taskSheet[cell] ||= {};
						this.taskSheet[cell].t = 's';
						this.taskSheet[cell].v = moment(outputDateTime).format(this.dateFormat + timeFormat);
					}

					// write the Touchplan Ticket URL
					if (this.tpTicketUrl) {
						this.taskSheet[`${this.tpTicketUrl.letter}${j + 1}`] ||= {};
						this.taskSheet[`${this.tpTicketUrl.letter}${j + 1}`].v = ticketUrl;
					}

					// store all ids for data integrity check
					if (this.externalScheduleIdMap.has(externalScheduleId)) {
						this.externalScheduleIdMap.get(externalScheduleId).push({
							id: externalScheduleId,
							activityName: ticket.activityName,
							planId: data.planId,
							ticketUrl,
						});
					} else {
						this.externalScheduleIdMap.set(externalScheduleId, [
							{
								id: externalScheduleId,
								activityName: ticket.activityName,
								planId: data.planId,
								ticketUrl,
							},
						]);
					}

					found = true;
					this.matchCount++;
					break;
				}
			}

			if (!found) {
				// external schedule id not found, report it!
				this.exception$.next({
					type: SpreadsheetExceptionType.MissingId,
					// message will be decorated in external-schedule.service
					data: {
						planId: data.planId,
						externalScheduleId,
						ticketUrl,
						activityName: (ticket as TicketFields).activityName,
					},
				});
			}
		}
	}

	/**
	 * Post-process
	 * The control is inverted and invoked in ticket stream observable complete
	 */
	public override complete(): void {
		if (this.processComplete$) {
			this.processComplete$.next(this.matchCount);
		}

		if (this.externalScheduleIdMap) {
			// check duplicate external schedule ids
			this.externalScheduleIdMap.forEach((tickets, externalScheduleId) => {
				if (tickets.length > 1) {
					for (const ticket of tickets) {
						this.exception$.next({
							type: SpreadsheetExceptionType.DuplicateId,
							data: {
								planId: ticket.planId,
								activityName: ticket.activityName,
								externalScheduleId,
								ticketUrl: ticket.ticketUrl,
							},
						});
					}
				}
			});
		}
	}

	/**
	 * Export and download the spreadsheet, uses native browser download
	 * @param filename filename to download
	 */
	public override export(filename: string): void {
		writeFile(this.workbook, filename);
	}

	/**
	 * Destroy/cleanup current instance
	 */
	public override destroy(): void {
		super.destroy();
		this.ticketToUpdate$.complete();
		this.ticketToUpdate$ = null;
		this.externalScheduleIdMap = null;
		this.taskSheet = null;
		this.matchCount = null;
		this.activityIdColumn = null;
		this.finishColumn = null;
		this.tpFinishColumn = null;
		this.tpTicketUrl = null;
	}

	protected parse(file: string, inputType: SpreadsheetInputType): ISpreadsheetValidation {
		const readOptions: object = {
			type: inputType,
			cellText: false,
			cellDates: true,
		};

		try {
			this.workbook = read(file, readOptions);
			this.sheet = utils.sheet_to_json(this.workbook.Sheets[SheetName.Task], {
				header: 1, // include both headers
				raw: false,
				dateNF: this.P6_DATE_FORMAT,
				blankrows: false,
			});
		} catch (error: unknown) {
			return {
				// don't throw, return it so UI/caller can ultimately show the error
				pass: false,
				message: 'External Schedule Error',
			};
		}

		// validate required sheets
		const requiredSheetValidation = this.validateRequiredSheets(this.workbook);
		if (requiredSheetValidation.pass === false) {
			return requiredSheetValidation;
		}

		// validate required columns, first 2 rows are header
		const requiredColumnValidation = this.validateRequiredColumns(this.sheet);
		if (requiredColumnValidation.pass === false) {
			return requiredColumnValidation;
		}
		return { pass: true };
	}

	private validateRequiredSheets(workbook: WorkBook): ISpreadsheetValidation {
		const sheetNames = workbook.SheetNames.map((name) => name.toUpperCase());
		let missingSheets: RequiredFields[] = [];
		missingSheets = this.checkField(sheetNames, SheetName.Task, missingSheets);
		missingSheets = this.checkField(sheetNames, SheetName.UserData, missingSheets);
		const isValid = missingSheets.length === 0;
		return {
			pass: isValid,
			message: isValid
				? null
				: `${EXTERNAL_SCHEDULE_SPREADSHEET_ERROR_MISSING_REQUIRED_SHEETS}: ${missingSheets.join(', ')}`,
		};
	}

	private validateRequiredColumns(sheet: Sheet): ISpreadsheetValidation {
		const columns: string[] = Object.values(sheet[1]);
		let missingColumns: RequiredFields[] = [];
		missingColumns = this.checkField(columns, ColumnName.ActivityId, missingColumns);
		missingColumns = this.checkField(columns, ColumnName.wBSCode, missingColumns);
		missingColumns = this.checkField(columns, ColumnName.Finish, missingColumns);
		missingColumns = this.checkField(columns, ColumnName.touchplanFinish, missingColumns);
		const isValid = missingColumns.length === 0;
		return {
			pass: isValid,
			message: isValid
				? null
				: `${EXTERNAL_SCHEDULE_SPREADSHEET_ERROR_MISSING_REQUIRED_COLUMNS}: ${missingColumns.join(', ')}`,
		};
	}

	private checkField(
		requiredFields: string[],
		fieldName: RequiredFields,
		missingFields: RequiredFields[]
	): RequiredFields[] {
		if (!requiredFields.includes(fieldName)) {
			missingFields.push(fieldName);
		}
		return missingFields;
	}

	private generateTpTicketUrl(projectId: string, planId: string, ticketId: string): string {
		return `${window.location.origin}/plan/${projectId}/${planId};ticketId=${ticketId}`;
	}
}
